import React, { useState } from 'react';
import { MapPin, Plus, Phone, Search, UtensilsCrossed } from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { formatAmount } from '../../utils/format';

import PullToRefresh from './PullToRefresh';

const statusStyles = {
  pending: 'bg-yellow-100 text-yellow-700',
  in_preparation: 'bg-blue-100 text-blue-700',
  ready: 'bg-green-100 text-green-700',
  delivered: 'bg-gray-100 text-gray-700',
  cancelled: 'bg-red-100 text-red-700',
  on_the_way: 'bg-purple-100 text-purple-700',
  nearby: 'bg-orange-100 text-orange-700',
  arrived: 'bg-teal-100 text-teal-700',
};

const statusLabels = {
  pending: 'Pendiente',
  in_preparation: 'En Preparación',
  ready: 'Lista',
  delivered: 'Completada',
  cancelled: 'Cancelada',
  on_the_way: 'En Camino',
  nearby: 'Cerca',
  arrived: 'Llegó',
};

const CustomerOrdersView = ({ myOrders, onReorder, onViewTracking, onLoadOrders, user, onRefresh, isRefreshing }) => {
  const [phoneSearch, setPhoneSearch] = useState('');

  const hasPhone = user?.phone || localStorage.getItem('customerInfo');

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('es-ES', {
        day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
      });
    } catch { return dateStr; }
  };

  if (!hasPhone && myOrders.length === 0) {
    return (
      <div className="px-4 py-8">
        <div className="text-center mb-6">
          <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Phone className="w-10 h-10 text-orange-400" />
          </div>
          <h2 className="text-lg font-bold text-gray-800">Mis Órdenes</h2>
          <p className="text-sm text-gray-500 mt-1">Ingresa tu teléfono para buscar tus órdenes</p>
        </div>
        <div className="max-w-sm mx-auto space-y-3">
          <Input
            type="tel"
            placeholder="Ej: 8099876543"
            value={phoneSearch}
            onChange={(e) => setPhoneSearch(e.target.value)}
            className="text-center text-lg rounded-xl h-12"
          />
          <Button
            onClick={() => onLoadOrders(phoneSearch)}
            disabled={!phoneSearch}
            className="w-full h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl"
          >
            <Search className="w-5 h-5 mr-2" />
            Buscar mis órdenes
          </Button>
        </div>
      </div>
    );
  }

  if (myOrders.length === 0) {
    return (
      <div className="text-center py-16 px-4">
        <UtensilsCrossed className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-600 font-medium">No tienes órdenes aún</p>
        <p className="text-sm text-gray-500 mt-1">Tus pedidos aparecerán aquí</p>
      </div>
    );
  }

  return (
    <PullToRefresh onRefresh={onRefresh} isRefreshing={isRefreshing}>
    <div className="pb-4">
      {/* Header */}
      <div className="bg-gradient-to-br from-orange-500 via-red-500 to-pink-500 -mx-4 -mt-4 px-4 pt-14 pb-6 mb-4 rounded-b-3xl shadow-lg">
        <h1 className="text-xl font-bold text-white">Mis Órdenes</h1>
        <p className="text-sm text-white/80">{myOrders.length} orden(es)</p>
      </div>

      <div className="space-y-3">
        {myOrders.map((order) => (
          <Card key={order.id} className="bg-white border border-gray-100 shadow-sm rounded-2xl overflow-hidden">
            <div className="p-4">
              {/* Header Row */}
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-bold text-gray-800">Orden #{order.order_number}</h3>
                  <p className="text-xs text-gray-500">{formatDate(order.created_at)}</p>
                </div>
                <Badge className={`${statusStyles[order.status] || 'bg-gray-100 text-gray-600'} text-xs font-medium`}>
                  {statusLabels[order.status] || order.status}
                </Badge>
              </div>

              {/* Items */}
              <div className="space-y-1.5 mb-3">
                {order.items?.slice(0, 3).map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span className="text-gray-700">{item.quantity}x {item.product_name}</span>
                    <span className="text-gray-600 font-medium">${formatAmount(item.subtotal)}</span>
                  </div>
                ))}
                {order.items?.length > 3 && (
                  <p className="text-xs text-gray-400">+{order.items.length - 3} más</p>
                )}
              </div>

              {/* Total */}
              <div className="border-t border-gray-100 pt-3 flex justify-between items-center mb-3">
                <span className="font-bold text-gray-800">Total</span>
                <span className="text-lg font-bold text-orange-600">${formatAmount(order.total)}</span>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                {['pending', 'in_preparation', 'ready', 'on_the_way', 'nearby', 'arrived'].includes(order.status) && (
                  <Button
                    onClick={() => onViewTracking(order.id)}
                    variant="outline"
                    className="flex-1 border-orange-200 text-orange-600 hover:bg-orange-50 rounded-xl h-10 text-sm"
                  >
                    <MapPin className="w-4 h-4 mr-1.5" />
                    Seguimiento
                  </Button>
                )}
                {['delivered', 'cancelled'].includes(order.status) && (
                  <Button
                    onClick={() => onReorder(order)}
                    className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-xl h-10 text-sm"
                  >
                    <Plus className="w-4 h-4 mr-1.5" />
                    Pedir de Nuevo
                  </Button>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
    </PullToRefresh>
  );
};

export default CustomerOrdersView;
